import { useEffect, useState, useRef } from 'react'
import { client, urlFor } from '../lib/sanity'
import Select from '../components/ui/Select'
import Button from '../components/ui/Button'

export default function Images() {
    const [images, setImages] = useState([])
    const [layout, setLayout] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('galleryLayout') || 'carousel'
        }
        return 'carousel'
    })
    const [fetchError, setFetchError] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [isLightboxOpen, setIsLightboxOpen] = useState(false)
    const [lightboxIndex, setLightboxIndex] = useState(0)

    const openLightbox = (index) => {
        setLightboxIndex(index)
        setIsLightboxOpen(true)
        document.documentElement.classList.add('overflow-hidden')
    }

    const closeLightbox = () => {
        setIsLightboxOpen(false)
        document.documentElement.classList.remove('overflow-hidden')
    }

    useEffect(() => {
        const fetchImages = async () => {
            setIsLoading(true)
            try {
                const data = await client.fetch(`*[_type == "imageGallery"] | order(_createdAt desc)`)
                setImages(data)
                setFetchError(!data || data.length === 0)
            } catch (err) {
                setFetchError(true)
                console.error("Failed to fetch images:", err)
            } finally {
                setIsLoading(false)
            }
        }
        fetchImages()
    }, [])

    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('galleryLayout', layout)
        }
    }, [layout])

    const renderImageCard = (item, index, className = '') => (
        <button
            type="button"
            onClick={() => openLightbox(index)}
            key={index}
            className={`relative group bg-white dark:bg-zinc-950 border-2 border-black dark:border-white shadow-brutal overflow-hidden text-left ${className}`}
            aria-label={`Open ${item.title || 'image'} in fullscreen`}
        >
            <img
                src={urlFor(item.image).width(900).height(600).url()}
                alt={item.title ? `${item.title}${item.description ? ` - ${item.description}` : ''}` : 'Gallery image'}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 flex flex-col justify-center items-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-white text-center px-6 py-4 backdrop-blur-sm">
                <h3 className="text-2xl font-bold drop-shadow-lg">{item.title}</h3>
                <p className="mt-3 text-sm sm:text-base max-w-sm">
                    {item.description?.length > 120 ? item.description.substring(0, 120) + '...' : item.description}
                </p>
            </div>
        </button>
    )

    function CarouselView({ images, isLightboxOpen }) {
        const [currentIndex, setCurrentIndex] = useState(0)
        const [autoSlide, setAutoSlide] = useState(true)
        const [isHovering, setIsHovering] = useState(false)
        const slideIntervalRef = useRef(null)
        const hasSetInitial = useRef(false)
        const touchStartX = useRef(null)
        const touchStartY = useRef(null)
        const touchDeltaX = useRef(0)

        const nextImage = () => setCurrentIndex((currentIndex + 1) % images.length)
        const prevImage = () => setCurrentIndex((currentIndex - 1 + images.length) % images.length)

        const onTouchStart = (e) => {
            const t = e.touches[0]
            touchStartX.current = t.clientX
            touchStartY.current = t.clientY
            touchDeltaX.current = 0
        }

        const onTouchMove = (e) => {
            if (touchStartX.current === null) return
            const t = e.touches[0]
            const dx = t.clientX - touchStartX.current
            const dy = t.clientY - touchStartY.current
            if (Math.abs(dy) > Math.abs(dx)) return
            touchDeltaX.current = dx
            e.preventDefault()
        }

        const onTouchEnd = () => {
            const dx = touchDeltaX.current
            touchStartX.current = null
            touchStartY.current = null
            touchDeltaX.current = 0
            const THRESHOLD = 50
            if (Math.abs(dx) > THRESHOLD) {
                setAutoSlide(false)
                if (dx < 0) {
                    nextImage()
                } else {
                    prevImage()
                }
            }
        }

        useEffect(() => {
            if (!images.length || hasSetInitial.current) return
            const idx = Math.floor(Math.random() * images.length)
            setCurrentIndex(idx)
            hasSetInitial.current = true
        }, [images.length])

        useEffect(() => {
            if (!autoSlide || isHovering || isLightboxOpen) return
            slideIntervalRef.current = setInterval(() => {
                setCurrentIndex(prev => (prev + 1) % images.length)
            }, 3000)
            return () => clearInterval(slideIntervalRef.current)
        }, [images.length, autoSlide, isHovering, isLightboxOpen])

        if (!images.length) return null

        return (
            <div className={`w-full max-w-8xl mx-auto px-4 ${isLightboxOpen ? 'hidden' : ''}`}>
                <div className="relative w-full flex justify-center items-center">
                    <button
                        onClick={() => {
                            setAutoSlide(false)
                            prevImage()
                        }}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition duration-300"
                    >
                        <span className="text-2xl">&#8592;</span>
                    </button>
                    <div
                        className="w-full h-[500px] sm:h-[600px] md:h-[700px] overflow-hidden relative max-w-4xl flex items-center justify-center"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                        style={{ touchAction: 'pan-y' }}
                    >
                        {images.map((img, index) => (
                            <div
                                key={index}
                                className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                            >
                                {renderImageCard(img, index)}
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => {
                            setAutoSlide(false)
                            nextImage()
                        }}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition duration-300"
                    >
                        <span className="text-2xl">&#8594;</span>
                    </button>
                </div>
                <div className="md:hidden flex justify-center mt-4">
                    <div className="text-white text-xs font-medium text-center">
                        <p className="opacity-70">Scroll to explore</p>
                        <span className="text-lg animate-bounce inline-block mt-1">↓</span>
                    </div>
                </div>
            </div>
        )
    }

    function GridView({ images }) {
        const [screenSize, setScreenSize] = useState('lg')
        const [page, setPage] = useState(0)

        const itemsPerPageMap = {
            'sm': 2,
            'md': 4,
            'lg': 6
        }
        const ITEMS_PER_PAGE = itemsPerPageMap[screenSize]

        useEffect(() => {
            const updateScreenSize = () => {
                if (window.innerWidth < 768) setScreenSize('sm')
                else if (window.innerWidth < 1024) setScreenSize('md')
                else setScreenSize('lg')
            }
            updateScreenSize()
            window.addEventListener('resize', updateScreenSize)
            return () => window.removeEventListener('resize', updateScreenSize)
        }, [])

        useEffect(() => {
            setPage(0)
        }, [screenSize])

        const totalPages = Math.ceil(images.length / ITEMS_PER_PAGE)
        const startIdx = page * ITEMS_PER_PAGE
        const pageImages = images.slice(startIdx, startIdx + ITEMS_PER_PAGE)
        const canPrev = page > 0
        const canNext = page < totalPages - 1

        return (
            <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {pageImages.map((item, i) => renderImageCard(item, startIdx + i, "break-inside-avoid"))}
                </div>
                {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-4 mt-8">
                        <Button variant="secondary" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={!canPrev}>
                            Previous
                        </Button>
                        <span className="font-mono font-medium text-zinc-800 dark:text-zinc-200">
                            Page {page + 1} of {totalPages}
                        </span>
                        <Button variant="secondary" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={!canNext}>
                            Next
                        </Button>
                    </div>
                )}
            </div>
        )
    }

    useEffect(() => {
        if (!isLightboxOpen) return
        const onKey = (e) => {
            if (e.key === 'Escape') closeLightbox()
            if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev - 1 + images.length) % images.length)
            if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev + 1) % images.length)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [isLightboxOpen, images.length])

    const LoadingSpinner = () => (
        <div className="flex flex-col items-center justify-center py-20">
            <div className="relative w-16 h-16 mb-4">
                <div className="absolute inset-0 rounded-full border-4 border-zinc-300 dark:border-zinc-700"></div>
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-accent animate-spin"></div>
            </div>
            <p className="text-zinc-600 dark:text-zinc-400 font-medium">Loading images.</p>
        </div>
    )

    return (
        <div className="min-h-screen bg-white dark:bg-zinc-950">
            <section className="max-w-7xl mx-auto px-4 py-12">
                <h2 className="text-4xl font-serif font-bold text-center mb-6 text-black dark:text-white">Image Gallery</h2>
                <div className="mb-6 flex items-center justify-center gap-2">
                    <label htmlFor="layout" className="font-medium text-zinc-800 dark:text-zinc-200">View:</label>
                    <Select
                        id="layout"
                        value={layout}
                        onChange={(e) => setLayout(e.target.value)}
                        wrapperClassName="w-auto"
                    >
                        <option value="grid">Grid</option>
                        <option value="carousel">Carousel</option>
                    </Select>
                </div>

                {isLoading ? (
                    <LoadingSpinner />
                ) : fetchError ? (
                    <div className="text-center text-xl text-zinc-500 dark:text-zinc-400 py-10">
                        <p>No images to display.</p>
                    </div>
                ) : layout === 'carousel' ? (
                    <CarouselView images={images} isLightboxOpen={isLightboxOpen} />
                ) : (
                    <GridView images={images} />
                )}
            </section>

            {isLightboxOpen && images.length > 0 && (
                <div
                    className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={closeLightbox}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Image lightbox"
                >
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            setLightboxIndex(prev => (prev - 1 + images.length) % images.length)
                        }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
                        aria-label="Previous image"
                        title="Previous (←)"
                    >
                        <span className="text-2xl">&#8592;</span>
                    </button>

                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            setLightboxIndex(prev => (prev + 1) % images.length)
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
                        aria-label="Next image"
                        title="Next (→)"
                    >
                        <span className="text-2xl">&#8594;</span>
                    </button>

                    <div className="absolute top-4 left-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm">
                        {lightboxIndex + 1} / {images.length}
                    </div>

                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            closeLightbox()
                        }}
                        className="absolute top-4 right-4 w-12 h-12 md:w-10 md:h-10 flex items-center justify-center bg-white/10 hover:bg-accent hover:text-black text-white border border-white/50 transition-colors duration-200"
                        aria-label="Close lightbox"
                        title="Close (Esc)"
                    >
                        <span className="text-3xl md:text-2xl font-light">✕</span>
                    </button>

                    <div className="hidden md:block absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm">
                        Press <kbd className="bg-black/40 px-1.5 py-0.5 rounded text-white">Esc</kbd> to exit
                    </div>

                    <div
                        className="max-w-[95vw] max-h-[95vh] w-full flex flex-col items-center gap-3 relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            key={lightboxIndex}
                            src={urlFor(images[lightboxIndex].image).url()}
                            alt={images[lightboxIndex]?.title ? `${images[lightboxIndex].title}${images[lightboxIndex]?.description ? ` - ${images[lightboxIndex].description}` : ''}` : 'Gallery image'}
                            className="w-auto h-auto max-w-[95vw] max-h-[80vh] object-contain border-2 border-white/20"
                            loading="eager"
                        />
                        <div className="w-full text-center text-white/90 px-2">
                            {images[lightboxIndex]?.title && (
                                <div className="text-lg font-semibold leading-tight">{images[lightboxIndex].title}</div>
                            )}
                            {images[lightboxIndex]?.description && (
                                <div className="text-sm opacity-90 mt-1 max-h-[12vh] overflow-auto">
                                    {images[lightboxIndex].description}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}